// The shell routes every screen hangs under. Features attach their routes with
// `getParentRoute: () => teamRoute` (or brandRoute), and src/app/router.tsx assembles the tree.
// The team and the brand live in the URL and nowhere else, so switching them is a navigation.
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  notFound,
  Outlet,
  redirect,
} from "@tanstack/react-router";

import { findTeam, hasBrand } from "../api/session";
import { AppLayout } from "./AppLayout";
import { readPref } from "./prefs";
import { RouteErrorScreen } from "./RouteErrorScreen";
import { NoTeamScreen, NotFoundScreen } from "./screens";
import { requireSession } from "./session";
import { TeamLayout } from "./TeamLayout";

export interface RouterContext {
  queryClient: QueryClient;
}

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    /**
     * The route works without a session (sign-in, the invitation link): an `UNAUTHENTICATED`
     * answer there is the page's to handle, and the ended-session handler leaves it alone.
     */
    public?: boolean;
    /**
     * The page covers the app's frame (the video editor, as live's has the whole window): the
     * shell's header and sidebar are inert under it, out of the tab order and of a screen
     * reader's reading.
     */
    fullWindow?: boolean;
    /**
     * The page has no team of its own but shows in a team's frame, with its sidebar (the staff
     * pages, as live's Admin Panel keeps the app's frame): the team this tab was last on, else
     * the first team, so opening the page changes nothing around it (`AppLayout`).
     */
    teamFrame?: boolean;
    /**
     * The page draws its own frame (the Pro Studio redesign, spec ui-perf/pro-studio-redesign):
     * the team's layout keeps its stream, media cookies and theme but draws no `Shell`, and the
     * page brings its own header, nav and `#main`.
     */
    ownFrame?: boolean;
  }
}

/** Whether the router is on a route that works without a session. */
export function onPublicRoute(matches: readonly { staticData: { public?: boolean } }[]): boolean {
  return matches.some((match) => match.staticData.public === true);
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: string): boolean {
  return UUID.test(value);
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  errorComponent: RouteErrorScreen,
  notFoundComponent: NotFoundScreen,
});

/** Every page behind sign-in sits under this pathless layout, which owns the session guard. */
export const signedInRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "signed-in",
  beforeLoad: async ({ context, location }) => ({
    scope: await requireSession(context.queryClient, location),
  }),
  component: AppLayout,
});

/**
 * Any other path sits behind the guard too, as live's catch-all does: without a session it goes
 * to sign-in and comes back after; signed in, it is not found, in the app's frame (issue 294).
 */
export const unknownRoute = createRoute({
  getParentRoute: () => signedInRoute,
  path: "$",
  component: NotFoundScreen,
});

/**
 * `/` opens the first team in the UI this browser chose (spec ui-perf/ui-switch-classic-simple-pro):
 * Classic's team home, or the team's way into Pro, which opens Simple when it is the choice. A user
 * in no team sees what to do, never an endless loader.
 */
export const indexRoute = createRoute({
  getParentRoute: () => signedInRoute,
  path: "/",
  beforeLoad: ({ context }) => {
    const first = context.scope.teams[0];
    if (first === undefined) return;
    const params = { teamId: first.team_id };
    if (readPref("ui") === "classic") throw redirect({ to: "/teams/$teamId", params });
    throw redirect({ to: "/teams/$teamId/pro", params });
  },
  component: NoTeamScreen,
});

// A param that is not a UUID does not match, so the root renders not-found and no request
// carries a malformed id. A team or brand outside the scope is not found either, never forbidden,
// as the API answers (proposal 6.2).
export const teamRoute = createRoute({
  getParentRoute: () => signedInRoute,
  path: "teams/$teamId",
  params: { parse: ({ teamId }) => (isUuid(teamId) ? { teamId } : false) },
  beforeLoad: ({ context, params }) => {
    const team = findTeam(context.scope, params.teamId);
    if (team === undefined) throw notFound();
    return { team };
  },
  component: TeamLayout,
});

export const brandRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: "brands/$brandId",
  params: { parse: ({ brandId }) => (isUuid(brandId) ? { brandId } : false) },
  beforeLoad: ({ context, params }) => {
    if (!hasBrand(context.team, params.brandId)) throw notFound();
  },
});
